import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import { UsersResultsShare } from '@sketch-note/design-system/src/components/share';
import { DownArrowIcon } from '@sketchnote/icons';
import {
  AccessDropdown,
  Button,
  InvitePending,
  InviteUsers,
  MoreActionMenu,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { API, useSocketSyncOrg } from 'src/api';
import { useSettings as useSettingsHook } from 'src/api/hooks/fetch-settings';
import { isFreeUser, PAYPLAN_COST, PAYPLAN_TIMELINE } from 'src/constants/plans';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useSettings } from '../store';
import { useAccountStore, useManageMembersStore } from '../store';
import { GuestAccess } from './guest-access';
import { RemoveUser } from './remove-user';
export interface Member {
  memberId: string;
  firstname: string;
  email: string;
  userType: string;
  avatar?: string;
  _id?: string;
  role?: string;
  default?: boolean;
}
export interface Guest {
  files: { title: string; id?: string; type?: string; icon?: JSX.Element }[];
  member: Member;
}

export const ManageMembers = ({ onClose }: { onClose: () => void }): JSX.Element => {
  const [isRemoveUserOpen, setIsRemoveUserOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteUserList, setInviteUserList] = useState<any>();
  const [isInviteConfirmationOpen, setIsInviteConfirmationOpen] = useState(false);
  const [deleteMember, setDeleteMember] = useState('');
  const [isDeleteInviteOpen, setIsDeleteInviteOpen] = useState({ email: '', memberId: '' });
  const [callbacks] = useSettings();
  const { revalidate } = useSettingsHook();
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const accessMenuItems = [
    { title: 'Admin', value: 'admin', subTitle: 'Can manage team settings' },
    { title: 'Member', value: 'member', subTitle: 'Cannot change workspace settings' },
    { title: 'Remove', value: 'remove', subTitle: 'Will remove all team access' },
  ];
  const accessMenuItemsOwner = [
    { title: 'Admin', value: 'admin', subTitle: 'Can manage team settings' },
    { title: 'Member', value: 'member', subTitle: 'Cannot change workspace settings' },
  ];

  const accessMenuItemsGuest = [
    { title: 'Guest', value: 'guest', subTitle: 'Has access to selected files' },
    { title: 'Remove', value: 'remove', subTitle: 'Will remove access' },
  ];

  const {
    admin,
    organizationmembers,
    setOrganizationMembers,
    memberCount,
    isOwner,
    owner,
    guests,
    pendingInvitesGuests,
    pendingInvitesMembers,
  } = useManageMembersStore((state) => state);

  const { organization } = useAccountStore((state) => state);

  const [convertedMember, setConvertedMember] = useState('');
  const [openSnackbar] = useSnackbar();
  const { onCloseSettings } = useLayout();

  const handleAccessClick = async (id: string, value: string, mode?: string) => {
    if (value === 'remove') {
      if (mode === 'invite-pending') {
        const response = await API.delete(`/settings/invite?id=${id}`);
        if (response.data.success) {
          revalidate();
          openSnackbar({
            text: 'Invite deleted successfully',
            snackType: 'standard',
            position: 'bottom-center',
          });
        }
      } else {
        setIsRemoveUserOpen(true);
        setDeleteMember(id);
      }
    } else if (value === 'admin' || value === 'member') {
      if (mode === 'invite-pending') {
        const payload = {
          role: value,
          id,
        };
        const response = await API.put('/settings/invite', payload);
        if (response.data.success) {
          openSnackbar({
            text: 'Updated sucessfully',
            snackType: 'standard',
            position: 'bottom-center',
          });
          revalidate();
        }
      } else {
        const payload = {
          role: value,
          memberId: id,
        };
        callbacks?.onUpdateMemberAccess?.(payload);
      }
    }
  };
  const handleResendInvite = async (memberId: string) => {
    const response = await API.put('/settings/invite', {
      id: memberId,
      resend: '1',
    });
  };

  const handleDeleteInvite = async (email: string, memberId: string) => {
    setIsDeleteInviteOpen({ email, memberId });
  };

  const deleteInvite = async () => {
    const response = await API.delete(`/settings/invite?id=${isDeleteInviteOpen.memberId}`);
    openSnackbar({
      text: 'Invite deleted successfully',
      snackType: 'standard',
      position: 'bottom-center',
    });
    setIsDeleteInviteOpen({ email: '', memberId: '' });
    revalidate();
  };

  const handleRemoveUserModalClose = () => {
    setIsRemoveUserOpen(false);
    setDeleteMember('');
  };

  const handleInviteUsers = async () => {
    setIsInviteOpen(false);
    setIsInviteConfirmationOpen(false);
    const payload = {
      role: inviteUserList?.access?.value,
      emails: inviteUserList?.items?.map((obj) => obj.email),
    };
    callbacks?.onInviteUsers?.(payload);
  };

  const handleRemoveUser = () => {
    callbacks?.onRemoveUser?.(deleteMember);
    setIsRemoveUserOpen(false);
  };

  const authData = useAuth((s) => s.userDetails);

  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);

  const socketSyncOrg = useSocketSyncOrg({
    organisation: authData?.memberInformation?.organisation?.organisationId,
  });

  const handleConvertMember = async (memberId) => {
    const response = await callbacks?.onConvertUser?.(memberId);

    if (response.isSuccess) {
      openSnackbar({
        text: `${convertedMember} is now a member`,
        snackType: 'standard',
        position: 'bottom-center',
      });
      setConvertedMember('');
    }
  };

  const handleRemoveGuest = (memberId) => {
    callbacks?.onRemoveUser?.(memberId);
  };

  useEffect(() => {
    const getUserDetails = async () => {
      const userDetails = await fetchUserDetails();

      userDetails?.organisations.map((organisation) => {
        if (organisation.organisationId === (socketSyncOrg as any)?.sync?.organisation) {
          setOrganizationMembers(organisation.members);
        }
      });
    };
    getUserDetails();
  }, [socketSyncOrg]);

  const handleGuestFileRoute = ({ type, id }) => {
    const routes = {
      workspace: `/${mode}/${orgId}/w/${id}`,
      file: `/${mode}/${orgId}/files/${id}`,
      board_task: `/${mode}/${orgId}/tasks/${id}?type=${router.query.type}`,
    };
    return routes[type];
  };

  return (
    <div className="px-48 mb-20 overflow-y-scroll no-scrollbar" style={{ height: '57rem' }}>
      <section>
        <h3 className="font-medium leading-32 text-base mb-8 text-gray-999 pt-16">
          {organization}
        </h3>
        <p className="text-sm mb-16 text-gray-600">Add and assign member roles here</p>
        {authData?.plan.type && isFreeUser(authData?.plan.type) ? (
          <Tippy
            className="w-auto h-auto bg-gray-999"
            content={'Upgrade your Sketchnote plan to add members to your organization'}
            arrow={true}
          >
            <span className="inline-block">
              <button
                onClick={() => {
                  onClose();
                  router.push(`/${mode}/${orgId}/pricing`);
                }}
                className="bg-blue-500 text-center text-white py-6 px-16 rounded-sm font-medium"
              >
                Upgrade
              </button>
            </span>
          </Tippy>
        ) : !admin ? (
          <Tippy
            className="w-auto h-auto bg-gray-999"
            content={'Only admins can add members'}
            arrow={true}
          >
            <span className="inline-block">
              <button
                disabled={true}
                className="bg-gray-400 text-center text-white py-6 px-16 rounded-sm font-medium"
              >
                Add members
              </button>
            </span>
          </Tippy>
        ) : (
          <button
            className="bg-blue-500 text-center text-white py-6 px-16 rounded-sm font-medium"
            onClick={() => setIsInviteOpen(true)}
          >
            Add members
          </button>
        )}
      </section>
      <section className="mt-40 overflow-scroll">
        <h3 className="font-regular leading-32 text-base text-gray-500 mb-16">
          Members in {organization} ({memberCount})
        </h3>

        <div className="flex justify-between pb-16 border-b-2 border-gray-100 text-gray-999">
          <div className="text-sm font-regular">Users</div>
          {admin && <div className="text-sm font-regular">Permissions</div>}
        </div>
        <div className="overflow-y-auto max-h-275  pt-24 no-scrollbar">
          {organizationmembers.map(({ email, firstname, memberId, avatar, role }) => (
            <div key={memberId} className="flex justify-between items-center -ml-16 mr-8">
              <UsersResultsShare
                className="members"
                avatar={avatar}
                email={email}
                username={firstname}
                id={memberId}
              />

              <AccessDropdown
                accessLevel={role}
                menuItems={owner === memberId ? accessMenuItemsOwner : accessMenuItems}
                onClickMenuItem={(values) => handleAccessClick(memberId, values)}
                isReadOnly={!admin}
              />
            </div>
          ))}

          <div className="-mt-10">
            {pendingInvitesMembers.map(({ email, role, _id: memberId }) => (
              <InvitePending
                key={email}
                email={email}
                role={role}
                resendInvite
                deleteInvite
                memberId={memberId}
                isReadOnly={!admin}
                handleResendInvite={handleResendInvite}
                handleDeleteInvite={handleDeleteInvite}
                accessMenuItems={accessMenuItems}
                onClickAccessMenuItem={(values) =>
                  handleAccessClick(memberId || '', values, 'invite-pending')
                }
              />
            ))}
          </div>
        </div>

        {(guests?.length > 0 || pendingInvitesGuests?.length > 0) && (
          <>
            <h3 className="font-regular leading-32 text-base text-gray-500 mb-16 mt-32">
              Guest ({guests?.length})
            </h3>
            <div className="flex justify-between pb-16 border-b-2 border-gray-100">
              <div className="text-sm font-regular">User</div>
              <div className="text-sm font-regular">Access level</div>
            </div>
            <div className="max-h-275 pt-24 overflow-y-auto no-scrollbar">
              {guests?.map(({ member, files }) => (
                <div
                  key={member?.memberId}
                  className="flex justify-between items-center -ml-16 mr-8"
                >
                  <UsersResultsShare
                    className="members"
                    avatar={member?.avatar}
                    email={member?.email}
                    username={member?.firstname}
                    id={member?.memberId}
                  />

                  <div className="flex text-gray-600 items-center text-sm">
                    <span className="mr-8">{files?.length} access</span>

                    <MoreActionMenu
                      headerData={
                        <div className="py-12">
                          <div className="flex text-sm">
                            <Button
                              status="primary"
                              onClick={() => {
                                setConvertedMember(member?.firstname);
                                handleConvertMember(member?.memberId);
                              }}
                              className="mr-8"
                            >
                              Convert to member
                            </Button>
                            <Button
                              status="danger"
                              onClick={() => handleRemoveGuest(member?.memberId)}
                            >
                              Remove
                            </Button>
                          </div>
                          <span className="text-xs text-gray-500 font-medium block pt-16">
                            {files.length > 0
                              ? 'This guest has access to these files workspaces and tasks'
                              : files.length === 0
                              ? 'This guest has access to no files workspaces and tasks'
                              : ''}
                          </span>
                        </div>
                      }
                      classNames={{ menuWrap: 'p-12 h-197 overflow-y-scroll' }}
                      menuItems={files?.map(({ title, type, icon, id }) => ({
                        label: <GuestAccess title={title} subTitle={type} />,
                        value: handleGuestFileRoute({ type, id }),
                      }))}
                      icon={<DownArrowIcon wrapperClass="mt-4" />}
                      onClickMenuItem={(e) => {
                        onCloseSettings();
                        router.push(e.value as string);
                      }}
                    />
                  </div>
                </div>
              ))}
              <div className="-mt-10">
                {pendingInvitesGuests?.map(({ email, role, _id: memberId }) => (
                  <InvitePending
                    email={email}
                    role={role}
                    key={email}
                    resendInvite
                    deleteInvite
                    isReadOnly={!admin}
                    memberId={memberId}
                    accessMenuItems={accessMenuItemsGuest}
                    handleResendInvite={handleResendInvite}
                    handleDeleteInvite={handleDeleteInvite}
                    onClickAccessMenuItem={(values) =>
                      handleAccessClick(memberId || '', values, 'invite-pending')
                    }
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </section>
      <Modal isOpen={isInviteOpen && admin} onClose={() => setIsInviteOpen(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="50rem"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalBody className="flex" p="0">
            <div className="w-full">
              {authData?.plan?.type && (
                <InviteUsers
                  members={[]}
                  accessLevels={[
                    { label: 'Admin', value: 'admin' },
                    { label: 'Member', value: 'member' },
                  ]}
                  onSubmit={(values) => {
                    setInviteUserList(values);
                    setIsInviteConfirmationOpen(true);
                  }}
                  memberRate={{
                    time: PAYPLAN_TIMELINE[authData?.plan?.type],
                    cost: PAYPLAN_COST[authData?.plan?.type],
                  }}
                  memberType={'member'}
                  isFreeTrial={!!authData?.plan?.trial}
                />
              )}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isRemoveUserOpen} onClose={handleRemoveUserModalClose}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '31.2rem',
            borderRadius: '0.8rem',
            margin: 'auto',
            backgroundColor: 'white',
            padding: '24px',
          }}
        >
          <ModalCloseButton className="text-xs text-gray-500 mb-24" />
          <ModalBody>
            <RemoveUser
              members={organizationmembers}
              deleteMember={deleteMember}
              workspaceName={organization}
              handleRemoveUserModalClose={handleRemoveUserModalClose}
              handleRemoveUser={handleRemoveUser}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isInviteConfirmationOpen} onClose={() => setIsInviteConfirmationOpen(false)}>
        <ModalOverlay />
        <ModalContent
          maxWidth="50rem"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalBody className="flex" p="0">
            <ModalContent
              style={{
                maxWidth: '46rem',
                padding: '32px',
                marginTop: '20rem',
                borderRadius: '8px',
              }}
            >
              <ModalCloseButton />
              <p className="mb-24 text-md text-gray-999">Add new members?</p>
              <p className="mb-32 text-sm text-gray-999">
                You will be charged on a pro-rated basis for each new member added to your
                organization. This will be over and above your current plan already active.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsInviteConfirmationOpen(false);
                  }}
                  className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
                >
                  Cancel
                </button>
                <button
                  className="bg-blue-500 text-white border rounded-sm px-24 py-13"
                  onClick={handleInviteUsers}
                >
                  Confirm & send invite
                </button>
              </div>
            </ModalContent>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDeleteInviteOpen.memberId.length > 0}
        onClose={() => setIsDeleteInviteOpen({ email: '', memberId: '' })}
      >
        <ModalOverlay />
        <ModalContent
          maxWidth="50rem"
          borderRadius="0.8rem"
          className="bg-white static rounded-lg"
          margin="auto"
        >
          <ModalBody className="flex" p="0">
            <ModalContent
              style={{
                maxWidth: '46rem',
                padding: '32px',
                marginTop: '20rem',
                borderRadius: '8px',
              }}
            >
              <ModalCloseButton />
              <p className="mb-24 text-md text-gray-999">
                Delete invite for {isDeleteInviteOpen.email}?
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsDeleteInviteOpen({ email: '', memberId: '' });
                  }}
                  className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
                >
                  Cancel
                </button>
                <button
                  className="bg-red-500 text-white border rounded-sm px-24 py-13"
                  onClick={deleteInvite}
                >
                  Confirm & delete invite
                </button>
              </div>
            </ModalContent>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};
